import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, List, X } from '@phosphor-icons/react';

const LOGO_URL = "/images/logo.jpg";

const navLinks = [
  { label: 'Accueil', href: '#accueil' },
  { label: 'À propos', href: '#apropos' },
  { label: 'Services', href: '#services' },
  { label: 'Témoignages', href: '#temoignages' },
  { label: 'Contact', href: '#contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-sm border-b border-black/5' : 'bg-transparent'
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a 
              href="#accueil" 
              onClick={(e) => handleNavClick(e, '#accueil')}
              className="flex items-center"
              data-testid="header-logo"
            >
              <img 
                src={LOGO_URL} 
                alt="Électricité Béchameil" 
                className="h-8 sm:h-10 md:h-12 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isScrolled ? 'text-[#0A0A0A] hover:text-[#D32F2F]' : 'text-[#0A0A0A] hover:text-[#D32F2F]'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center">
              <a
                href="tel:0629676879"
                className="inline-flex items-center gap-2 bg-[#D32F2F] text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#B71C1C] transition-colors duration-200"
                data-testid="header-cta-phone"
              >
                <Phone weight="fill" className="w-4 h-4" />
                <span>06 29 67 68 79</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-black/5 transition-colors"
              aria-label="Menu"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#0A0A0A]" weight="bold" />
              ) : (
                <List className="w-6 h-6 text-[#0A0A0A]" weight="bold" />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 mobile-menu-overlay lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 right-0 bottom-0 w-[280px] bg-white z-50 shadow-2xl lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-black/5">
                  <img 
                    src={LOGO_URL} 
                    alt="Électricité Béchameil" 
                    className="h-8 w-auto"
                  />
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                    aria-label="Fermer le menu"
                  >
                    <X className="w-5 h-5 text-[#0A0A0A]" weight="bold" />
                  </button>
                </div>
                <nav className="flex-1 p-4">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="block py-3 text-base font-medium text-[#0A0A0A] hover:text-[#D32F2F] transition-colors"
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="p-4 border-t border-black/5">
                  <a
                    href="tel:0629676879"
                    className="flex items-center justify-center gap-2 w-full bg-[#D32F2F] text-white rounded-full px-5 py-3 font-semibold hover:bg-[#B71C1C] transition-colors duration-200"
                    data-testid="mobile-cta-phone"
                  >
                    <Phone weight="fill" className="w-5 h-5" />
                    <span>Appeler maintenant</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
